/**
 * Authentication state management
 */

import { create } from 'zustand';
import type { User, TierLimits, SubscriptionTier } from '@frameit/types';

/** Auth state */
interface AuthState {
  // User state
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Usage
  usage: {
    projectCount: number;
    storageUsed: number;
    exportsThisMonth: number;
  } | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setUsage: (usage: AuthState['usage']) => void;
  logout: () => void;

  // Helpers
  getTierLimits: () => TierLimits;
  canCreateProject: () => boolean;
  canExport: () => boolean;
  canUseVideoExport: () => boolean;
  canUsePremiumTemplates: () => boolean;
  requiresWatermark: () => boolean;
}

/** Default tier limits for unauthenticated users */
const DEFAULT_LIMITS: TierLimits = {
  maxProjects: 0,
  maxExportResolution: 1024,
  videoExport: false,
  premiumTemplates: false,
  watermark: true,
  monthlyExports: 0,
  maxStorage: 0,
};

/** Tier limits mapping */
const LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    maxProjects: 3,
    maxExportResolution: 2048,
    videoExport: false,
    premiumTemplates: false,
    watermark: true,
    monthlyExports: 10,
    maxStorage: 100 * 1024 * 1024,
  },
  pro: {
    maxProjects: 100,
    maxExportResolution: 8192,
    videoExport: true,
    premiumTemplates: true,
    watermark: false,
    monthlyExports: 0, // unlimited
    maxStorage: 10 * 1024 * 1024 * 1024,
  },
};

// Dev mode - provide mock user when Clerk is not configured
const isDevMode = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const devUser = isDevMode ? {
  id: 'dev_user',
  email: 'dev@localhost',
  subscriptionTier: 'pro' as const,
  stripeCustomerId: undefined,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
} : null;

const devUsage = isDevMode ? {
  projectCount: 1,
  storageUsed: 0,
  exportsThisMonth: 0,
} : null;

/** Create auth store */
export const useAuthStore = create<AuthState>()((set, get) => ({
  user: devUser,
  isLoading: false,
  isAuthenticated: isDevMode,
  usage: devUsage,

  setUser: (user) => {
    set({
      user,
      isAuthenticated: !!user,
      isLoading: false,
    });
  },

  setLoading: (loading) => set({ isLoading: loading }),

  setUsage: (usage) => set({ usage }),

  logout: () => {
    set({
      user: null,
      isAuthenticated: false,
      usage: null,
    });
  },

  getTierLimits: () => {
    const { user } = get();
    if (!user) return DEFAULT_LIMITS;
    return LIMITS[user.subscriptionTier];
  },

  canCreateProject: () => {
    const { user, usage } = get();
    if (!user || !usage) return false;

    const limits = LIMITS[user.subscriptionTier];
    return usage.projectCount < limits.maxProjects;
  },

  canExport: () => {
    const { user, usage } = get();
    if (!user || !usage) return false;

    const limits = LIMITS[user.subscriptionTier];
    if (limits.monthlyExports === 0) return true; // unlimited
    return usage.exportsThisMonth < limits.monthlyExports;
  },

  canUseVideoExport: () => {
    const { user } = get();
    if (!user) return false;
    return LIMITS[user.subscriptionTier].videoExport;
  },

  canUsePremiumTemplates: () => {
    const { user } = get();
    if (!user) return false;
    return LIMITS[user.subscriptionTier].premiumTemplates;
  },

  requiresWatermark: () => {
    const { user } = get();
    if (!user) return true;
    return LIMITS[user.subscriptionTier].watermark;
  },
}));
